import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MousePointer, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { ACTIONS } from '../../../hooks/useWizardForm';
import StepHint from '../StepHint';

const LazyButtonMappingEditor = React.lazy(() => import('../ButtonMappingEditor'));

/**
 * Extracts all {fieldName} references from button mapping values.
 * Returns an array of { button, field, key } objects.
 */
function extractFieldReferences(buttonMappings) {
  const refs = [];
  const fieldPattern = /\{([^}]+)\}/g;

  if (!buttonMappings || typeof buttonMappings !== 'object') return refs;

  for (const [btnText, mapping] of Object.entries(buttonMappings)) {
    if (!mapping) continue;

    // Check params values
    if (mapping.params && typeof mapping.params === 'object') {
      for (const [paramKey, paramValue] of Object.entries(mapping.params)) {
        if (typeof paramValue === 'string') {
          let match;
          while ((match = fieldPattern.exec(paramValue)) !== null) {
            refs.push({ button: btnText, field: match[1], key: `params.${paramKey}` });
          }
        }
      }
    }

    // Check feedbackMessage
    if (typeof mapping.feedbackMessage === 'string') {
      let match;
      while ((match = fieldPattern.exec(mapping.feedbackMessage)) !== null) {
        refs.push({ button: btnText, field: match[1], key: 'feedbackMessage' });
      }
    }

    // Check errorMessage
    if (typeof mapping.errorMessage === 'string') {
      let match;
      while ((match = fieldPattern.exec(mapping.errorMessage)) !== null) {
        refs.push({ button: btnText, field: match[1], key: 'errorMessage' });
      }
    }
  }

  return refs;
}

export default function StepButtons({ state, dispatch, errors, availableFields, mktTemplates, connectors, connectorTypes, agents }) {
  const { t } = useTranslation('campaigns');
  const [calloutExpanded, setCalloutExpanded] = useState(true);
  // eslint-disable-next-line no-console
  console.log('[StepButtons] connectors received:', connectors?.length, 'templates:', mktTemplates?.length);

  const handleFollowUpToggle = () => {
    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { path: 'agentFollowUpEnabled', value: !state.agentFollowUpEnabled },
    });
  };

  const handleMappingsChange = (newMappings) => {
    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { path: 'buttonMappings', value: newMappings },
    });
  };

  // Find the selected template to get button info
  const selectedTemplate = useMemo(() => {
    if (!state.templateId || !mktTemplates) return null;
    return mktTemplates.find(
      (t) => t.templateId === state.templateId || t.id === state.templateId
    ) || null;
  }, [state.templateId, mktTemplates]);

  // Get template buttons
  const templateButtons = useMemo(() => {
    if (!selectedTemplate) return [];
    if (selectedTemplate.buttons && Array.isArray(selectedTemplate.buttons)) {
      return selectedTemplate.buttons.map((b) => (typeof b === 'string' ? b : b.text || b.label || ''));
    }
    if (selectedTemplate.components) {
      const btnComponent = selectedTemplate.components.find((c) => c.type === 'BUTTONS');
      if (btnComponent?.buttons) {
        return btnComponent.buttons.map((b) => b.text || b.label || '');
      }
    }
    return [];
  }, [selectedTemplate]);

  // Detect invalid field references
  const invalidFieldRefs = useMemo(() => {
    if (!availableFields || availableFields.length === 0) return [];
    const refs = extractFieldReferences(state.buttonMappings);
    return refs.filter((ref) => !availableFields.includes(ref.field));
  }, [state.buttonMappings, availableFields]);

  const hasNoButtons = !templateButtons.length;

  return (
    <Container>
      <StepHint
        icon={MousePointer}
        title={t('stepButtons.hintTitle')}
        description={t('stepButtons.hintDescription')}
      />

      {/* Agent follow-up toggle */}
      <ToggleSection>
        <ToggleRow onClick={handleFollowUpToggle}>
          <ToggleInfo>
            <ToggleLabel>{t('stepButtons.followUpLabel')}</ToggleLabel>
            <ToggleDescription>
              {t('stepButtons.followUpDescription')}
            </ToggleDescription>
          </ToggleInfo>
          <ToggleSwitch
            $active={!!state.agentFollowUpEnabled}
            role="switch"
            aria-checked={!!state.agentFollowUpEnabled}
            aria-label={t('stepButtons.followUpLabel')}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleFollowUpToggle();
              }
            }}
          >
            <ToggleThumb $active={!!state.agentFollowUpEnabled} />
          </ToggleSwitch>
        </ToggleRow>
      </ToggleSection>

      {/* Callout explaining deterministic vs agentic paths */}
      <Callout>
        <CalloutHeader onClick={() => setCalloutExpanded((prev) => !prev)}>
          <CalloutLeft>
            <Info size={16} color={theme.colors.info} />
            <CalloutTitle>{t('stepButtons.calloutTitle')}</CalloutTitle>
          </CalloutLeft>
          {calloutExpanded
            ? <ChevronUp size={16} color={theme.colors.textMuted} />
            : <ChevronDown size={16} color={theme.colors.textMuted} />
          }
        </CalloutHeader>
        {calloutExpanded && (
          <CalloutContent>
            {t('stepButtons.calloutContent')}
          </CalloutContent>
        )}
      </Callout>

      {/* Warning indicators for invalid field references */}
      {invalidFieldRefs.length > 0 && (
        <WarningBox>
          <AlertTriangle size={16} color={theme.colors.warning} />
          <WarningContent>
            <WarningTitle>{t('stepButtons.warningTitle')}</WarningTitle>
            <WarningList>
              {invalidFieldRefs.map((ref, idx) => (
                <WarningItem key={`${ref.button}-${ref.key}-${ref.field}-${idx}`}>
                  <WarningField>{`{${ref.field}}`}</WarningField>
                  <WarningContext>
                    {t('stepButtons.warningContext', { button: ref.button, key: ref.key })}
                  </WarningContext>
                </WarningItem>
              ))}
            </WarningList>
          </WarningContent>
        </WarningBox>
      )}

      {/* ButtonMappingEditor (lazy loaded) or empty state */}
      {hasNoButtons ? (
        <EmptyState>
          {t('stepButtons.emptyState')}
        </EmptyState>
      ) : (
        <React.Suspense fallback={<div>{t('stepButtons.loadingEditor')}</div>}>
          <LazyButtonMappingEditor
            buttonMappings={state.buttonMappings}
            onChange={handleMappingsChange}
            templateButtons={templateButtons}
            availableFields={availableFields}
            connectors={connectors || []}
            connectorTypes={connectorTypes || []}
            agents={agents || []}
            instanceId={state.instanceId}
            template={selectedTemplate}
          />
        </React.Suspense>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const ToggleSection = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.white};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  cursor: pointer;
`;

const ToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToggleLabel = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const ToggleDescription = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.snug};
`;

const ToggleSwitch = styled.div`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${(p) => (p.$active ? theme.colors.primary : theme.colors.gray300)};
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ToggleThumb = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 2px;
  left: ${(p) => (p.$active ? '18px' : '2px')};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const Callout = styled.div`
  background: ${theme.colors.infoLight};
  border: 1px solid ${theme.colors.infoBorder};
  border-radius: ${theme.radii.md};
  overflow: hidden;
`;

const CalloutHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  cursor: pointer;

  &:hover {
    filter: brightness(0.98);
  }
`;

const CalloutLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const CalloutTitle = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const CalloutContent = styled.div`
  padding: 0 ${theme.spacing[4]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.snug};
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.warningLight};
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.md};
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const WarningTitle = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const WarningList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WarningItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.xs};
`;

const WarningField = styled.code`
  font-family: ${theme.typography.fontMono};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.warning};
  font-weight: ${theme.typography.medium};
`;

const WarningContext = styled.span`
  color: ${theme.colors.textMuted};
`;

const EmptyState = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border-radius: ${theme.radii.md};
  text-align: center;
`;
